import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LuChevronDown, LuHouse, LuLayoutGrid, LuLogOut, LuMoon, LuSun, LuUserRound } from 'react-icons/lu';
import { useTheme } from '../../contexts/ThemeContext';
import Avatar from '../ui/Avatar';
import { GLASS_CHIP, GLASS_POPOVER, GLASS_PRESSABLE } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const itemClass = [
  'tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-font-medium',
  'tw-text-slate-700 tw-transition-colors hover:tw-bg-slate-900/[0.05] dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.07]',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
].join(' ');

const iconClass = 'tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400 dark:tw-text-slate-500';

/**
 * Avatar button with the account menu. On phones the top bar has no room for
 * the Hall/Home/theme buttons, so the menu carries them there (md:hidden).
 */
const UserMenu = ({ employeeData, imageUrl, onImageError, onSignOut, isSigningOut }) => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const fullName = employeeData ? `${employeeData.eName || ''} ${employeeData.eSurname || ''}`.trim() : '';

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={containerRef} className="tw-relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('shell.account')}
        className={`tw-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-full tw-p-1 tw-text-slate-700 xl:tw-pr-3 dark:tw-text-slate-200 ${GLASS_CHIP} ${GLASS_PRESSABLE}`}
      >
        <Avatar firstName={employeeData?.eName} lastName={employeeData?.eSurname} imageUrl={imageUrl} onImageError={onImageError} />
        <span className="tw-hidden tw-max-w-[9rem] tw-truncate tw-text-sm tw-font-medium xl:tw-block">{fullName || t('common.user')}</span>
        <LuChevronDown
          aria-hidden="true"
          className={`tw-hidden tw-h-4 tw-w-4 tw-text-slate-400 tw-transition-transform xl:tw-block ${open ? 'tw-rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.16 }}
            className="tw-absolute tw-right-0 tw-top-[calc(100%+0.6rem)] tw-z-20 tw-w-72 tw-max-w-[calc(100vw-1.5rem)] tw-origin-top-right"
          >
            <div role="menu" className={`tw-rounded-2xl tw-p-2 ${GLASS_POPOVER}`}>
              <div className="tw-flex tw-items-center tw-gap-3 tw-px-2.5 tw-pb-3 tw-pt-2">
                <Avatar firstName={employeeData?.eName} lastName={employeeData?.eSurname} imageUrl={imageUrl} size="lg" />
                <div className="tw-min-w-0">
                  <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{fullName || t('common.user')}</p>
                  <p className="tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{employeeData?.eMail || ''}</p>
                </div>
              </div>

              <div className="tw-space-y-0.5 tw-border-t tw-border-slate-200/70 tw-pt-2 dark:tw-border-white/10">
                <button type="button" role="menuitem" onClick={() => go('/profile')} className={itemClass}>
                  <LuUserRound aria-hidden="true" className={iconClass} />
                  {t('shell.profile')}
                </button>
                <button type="button" role="menuitem" onClick={() => go('/dashboard')} className={`${itemClass} lg:tw-hidden`}>
                  <LuHouse aria-hidden="true" className={iconClass} />
                  {t('shell.home')}
                </button>
                <button type="button" role="menuitem" onClick={() => go('/hall')} className={`${itemClass} lg:tw-hidden`}>
                  <LuLayoutGrid aria-hidden="true" className={iconClass} />
                  {t('shell.hall')}
                </button>
                <button type="button" role="menuitem" onClick={toggleTheme} className={`${itemClass} lg:tw-hidden`}>
                  {isDarkMode ? <LuSun aria-hidden="true" className={iconClass} /> : <LuMoon aria-hidden="true" className={iconClass} />}
                  {isDarkMode ? t('shell.themeLight') : t('shell.themeDark')}
                </button>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-1.5 lg:tw-hidden">
                  <span className="tw-text-sm tw-font-medium tw-text-slate-700 dark:tw-text-slate-200">{t('language.label')}</span>
                  <LanguageSwitcher layoutId="usermenu-language-pill" />
                </div>
              </div>

              <div className="tw-mt-2 tw-border-t tw-border-slate-200/70 tw-pt-2 dark:tw-border-white/10">
                <button
                  type="button"
                  role="menuitem"
                  onClick={onSignOut}
                  disabled={isSigningOut}
                  className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-font-semibold tw-text-rose-600 tw-transition-colors hover:tw-bg-rose-500/10 disabled:tw-opacity-60 dark:tw-text-rose-300 dark:hover:tw-bg-rose-400/10"
                >
                  <LuLogOut aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
                  {isSigningOut ? t('shell.signingOut') : t('shell.signOut')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

UserMenu.propTypes = {
  employeeData: PropTypes.shape({
    eName: PropTypes.string,
    eSurname: PropTypes.string,
    eMail: PropTypes.string,
  }),
  imageUrl: PropTypes.string,
  onImageError: PropTypes.func,
  onSignOut: PropTypes.func.isRequired,
  isSigningOut: PropTypes.bool,
};

export default UserMenu;
